import React from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
  CircularProgress,
  Box,
} from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { MAX_ROW_EXPORT } from "../../const/export";

interface ExportWarningDialogProps {
  open: boolean;
  totalRows: number;
  onClose: (confirmed: boolean) => void;
  isLoading?: boolean;
}

const ExportWarningDialog: React.FC<ExportWarningDialogProps> = ({
  open,
  totalRows,
  onClose,
  isLoading = false,
}) => {
  const handleClose = (confirmed: boolean) => {
    if (!isLoading) {
      onClose(confirmed);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={() => handleClose(false)} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: {
          border: "1px solid #ed6c02",
          borderRadius: "12px",
          boxShadow: "0 8px 32px rgba(237, 108, 2, 0.25)",
        }
      }}
      >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          color: "#ed6c02",
          fontWeight: "bold",
          fontSize: "22px",
        }}
      >
        <WarningAmberIcon sx={{ color: "#ed6c02", fontSize: "22px" }} />
        Large Data Export Warning
      </DialogTitle>
      <DialogContent sx={{ margin: "12px 0" }}>
        <DialogContentText sx={{ mb: 2, fontSize: "18px" }}>
          You are about to export <strong>{totalRows.toLocaleString()}</strong> rows of data.
        </DialogContentText>
        <Box
          sx={{
            bgcolor: "#fff4e5",
            border: "1px solid #ed6c02",
            borderRadius: 1,
            p: 2,
          }}
        >
          <DialogContentText sx={{ color: "#663c00", fontSize: "18px" }}>
            <strong>Please note:</strong>
            <ul style={{ margin: "8px 0 0 0", paddingLeft: "20px" }}>
              <li>
                <strong>Only the first {MAX_ROW_EXPORT.toLocaleString()} rows</strong> will be exported due to system limitations
              </li>
              <li>Large exports may take several minutes to complete</li>
              <li>Please do not close the browser or navigate away during export</li>
              <li>Consider filtering your data to reduce the export size if possible</li>
            </ul>
          </DialogContentText>
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button
          onClick={() => handleClose(true)}
          color="warning"
          variant="contained"
          autoFocus
          disabled={isLoading}
          startIcon={
            isLoading ? <CircularProgress size={16} color="inherit" /> : null
          }
          sx={{ fontSize: "16px" }}
        >
          {isLoading ? "Exporting..." : "Continue Export"}
        </Button>
        <Button
          onClick={() => handleClose(false)}
          color="inherit"
          disabled={isLoading}
          variant="outlined"
          sx={{ fontSize: "16px" }}
        >
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ExportWarningDialog;
